import { useState, type KeyboardEvent } from 'react'
import { getSearchResultId } from './SearchResultList'

type KeyboardNavigationOptions = {
  resultCount: number
  /** Opens the result at this index (called on Enter). */
  onOpenResult: (_index: number) => void
}

/** Keeps a number between 0 and `max`. */
const clampIndex = (index: number, max: number) => Math.min(Math.max(index, 0), max)

/** Scrolls a result row into view inside the modal when the arrow keys reach it. */
const scrollResultIntoView = (index: number) => {
  document.getElementById(getSearchResultId(index))?.scrollIntoView({ block: 'nearest' })
}

/**
 * Arrow keys move a selection through the search results and Enter opens it, like a
 * command palette. The first result is selected by default, so typing and pressing
 * Enter opens the best match. Mostly useful on desktop; phones simply tap a result.
 */
export const useSearchKeyboardNavigation = ({
  resultCount,
  onOpenResult,
}: KeyboardNavigationOptions) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const lastIndex = resultCount - 1

  /** Selects a result and makes sure it is visible. */
  const moveSelection = (nextIndex: number) => {
    const index = clampIndex(nextIndex, lastIndex)
    setActiveIndex(index)
    scrollResultIntoView(index)
  }

  /** Handles ↑, ↓ and Enter in the search field. */
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (resultCount === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveSelection(activeIndex + 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveSelection(activeIndex - 1)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      onOpenResult(clampIndex(activeIndex, lastIndex))
    }
  }

  /** Moves the selection back to the first result, e.g. after the query changes. */
  const resetActiveIndex = () => setActiveIndex(0)

  return { activeIndex, setActiveIndex, resetActiveIndex, handleKeyDown }
}
